import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MapPin, Mail, Send, CheckCircle } from 'lucide-react';
import { SEOHead } from '../components/common/SEOHead';
import { Breadcrumbs } from '../components/common/Breadcrumbs';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// NAP Data
const NAP = {
  name: 'REALimenta',
  location: 'Málaga, Andalucía, España',
  email: 'realimenta.naturalvet@gmail.com',
};

export const ContactoPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    petType: '',
    petName: '',
    message: '',
    consent: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.consent) {
      toast.error('Debes aceptar la política de privacidad');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setIsSubmitted(true);
        toast.success('¡Mensaje enviado correctamente!');
      } else {
        throw new Error('Error al enviar');
      }
    } catch (error) {
      toast.error('Error al enviar el mensaje. Inténtalo de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEOHead
        title="Contacto | REALimenta"
        description="¿Tienes alguna duda? Escríbeme y te respondo en menos de 24-48 horas. Base en Málaga, atención online en toda España."
        canonical="/contacto"
      />

      <section className="pt-12 pb-20 md:pb-28">
        <div className="section-container">
          <Breadcrumbs items={[{ label: 'Contacto' }]} />

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <h1 
                className="font-manrope text-4xl sm:text-5xl font-extrabold text-[#1A2E22] tracking-tighter mb-6"
                data-testid="contacto-h1"
              >
                ¿Tienes alguna{' '}
                <span 
                  className="bg-clip-text text-transparent"
                  style={{ backgroundImage: 'linear-gradient(135deg, #3D6B4F 0%, #8AAE7F 100%)' }}
                >
                  duda
                </span>
                ?
              </h1>
              <p className="text-text-secondary text-lg mb-8">
                Estoy encantado de escucharte. Cuéntame cómo es tu compañero y en qué puedo ayudarte. Te responderé personalmente, con el cuidado que ellos se merecen. Si quieres reservar una consulta, hazlo desde <a href="/como-funciona" className="text-brand-primary hover:underline font-medium">Cómo funciona</a>.
              </p>

              {isSubmitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="card-brand text-center py-12"
                  data-testid="contact-success"
                >
                  <CheckCircle className="w-16 h-16 text-brand-accent mx-auto mb-4" />
                  <h2 className="font-manrope text-2xl font-bold text-text-primary mb-2">
                    ¡Mensaje enviado!
                  </h2>
                  <p className="text-text-secondary">
                    Te responderé lo antes posible. Revisa tu bandeja de entrada.
                  </p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Nombre *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="input-brand"
                        placeholder="Tu nombre"
                        data-testid="contact-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="input-brand"
                        placeholder="tu@email.com"
                        data-testid="contact-email"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Teléfono</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        className="input-brand"
                        placeholder="+34 XXX XXX XXX"
                        data-testid="contact-phone"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="petType">Tipo de mascota *</Label>
                      <select
                        id="petType"
                        name="petType"
                        value={formData.petType}
                        onChange={handleChange}
                        required
                        className="w-full input-brand px-4"
                        data-testid="contact-pet-type"
                      >
                        <option value="">Selecciona...</option>
                        <option value="perro">Perro</option>
                        <option value="gato">Gato</option>
                        <option value="ambos">Ambos</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="petName">Nombre de tu mascota</Label>
                    <Input
                      id="petName"
                      name="petName"
                      value={formData.petName}
                      onChange={handleChange}
                      className="input-brand"
                      placeholder="¿Cómo se llama?"
                      data-testid="contact-pet-name"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Cuéntame tu caso *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="input-brand resize-none"
                      placeholder="Describe brevemente qué te gustaría mejorar o qué dudas tienes..."
                      data-testid="contact-message"
                    />
                  </div>

                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="consent"
                      name="consent"
                      checked={formData.consent}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, consent: checked }))}
                      data-testid="contact-consent"
                    />
                    <Label htmlFor="consent" className="text-sm text-text-secondary leading-relaxed cursor-pointer">
                      He leído y acepto la{' '}
                      <Link to="/privacidad" className="text-brand-primary hover:underline">
                        política de privacidad
                      </Link>
                      . Entiendo que mis datos serán tratados para gestionar mi consulta. *
                    </Label>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary w-full flex items-center justify-center gap-2"
                    data-testid="contact-submit"
                  >
                    {isSubmitting ? (
                      'Enviando...'
                    ) : (
                      <>
                        Enviar mensaje
                        <Send className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="lg:pl-12"
            >
              <div className="card-brand mb-8">
                <h2 className="font-manrope text-xl font-bold text-text-primary mb-6">
                  Información de contacto
                </h2>
                
                <ul className="space-y-6">
                  <li className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-brand-primary/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-brand-primary" />
                    </div>
                    <div>
                      <p className="font-semibold text-text-primary">Ubicación</p>
                      <p className="text-text-secondary">{NAP.location}</p>
                      <p className="text-brand-secondary text-sm mt-1">Atención online en toda España</p>
                    </div>
                  </li>
                  
                  <li className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-brand-primary/10 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-brand-primary" />
                    </div>
                    <div>
                      <p className="font-semibold text-text-primary">Email</p>
                      <a 
                        href={`mailto:${NAP.email}`}
                        className="text-text-secondary hover:text-brand-primary transition-colors"
                        data-testid="contact-info-email"
                      >
                        {NAP.email}
                      </a>
                    </div>
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ContactoPage;
